import type { FC } from "react"

import { FloatingPortal } from "@floating-ui/react"
import { AnimatePresence, motion } from "framer-motion"
import { useLocation } from "react-router-dom"
import { useRecoilState, useRecoilValue } from "recoil"
import styled from "styled-components"

import { DEVICE } from "~/constants/device-widths"
import {
  useNotificationMessages,
  notificationsState,
} from "~/containers/Proposal/state/notifications/notifications"

import { ReactComponent as CloseIcon } from "@svg/close.svg"
import { ReactComponent as WarningIcon } from "@svg/warning-outline.svg"

import { notificationBarIsOpenState } from "./QueryErrorModal"

const Container = styled(motion.div)`
  top: 0;
  left: 0;
  position: fixed;
  width: 100vw;
  z-index: 9999;
  display: flex;
  justify-content: center;
  padding: 10px var(--space-xs);
  border: 1px solid #0044ff48;
  border-top: none;
  backdrop-filter: blur(10px);
  background: #ffffff77;
  box-shadow: 0px 4px 8px rgba(0, 62, 121, 0.09);
  > section {
    gap: var(--space-xs);
    display: flex;
    width: 100%;
    max-width: calc(var(--max-width-desktop) - 105px);
    > main {
      display: flex;
      width: 100%;
      gap: var(--space-xs);
    }
  }
`

const MessagesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: var(--space-2xs);
  width: 100%;
  max-height: 50vh;
  overflow-y: auto;
  > div {
    width: 100%;
  }
`

const NotificationIconWrapper = styled(motion.div)`
  display: none;
  @media ${DEVICE.tablet} {
    display: flex;
  }
  border-radius: 5px;
  width: 35px;
  height: 35px;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  svg {
    width: 25px;
    height: 25px;
    path {
      fill: var(--danger);
    }
  }
`
const CloseIconWrapper = styled.div`
  position: relative;
  width: 25px;
  height: 25px;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  > svg {
    cursor: pointer;
    width: 15px;
    height: 15px;
    path {
      stroke: var(--blue);
    }
  }
`

export const NotificationBar: FC = () => {
  const notifications = useNotificationMessages()
  const [isOpen, setIsOpen] = useRecoilState(notificationBarIsOpenState)
  const location = useLocation()
  const sideBarIsOpen = location.pathname.includes(`/setup`)

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <Container
            initial={{
              y: `-100%`,
              x: sideBarIsOpen ? `300px` : `-50%`,
              left: sideBarIsOpen ? 0 : `50%`,
              borderRadius: sideBarIsOpen ? 0 : `0 0 8px 8px`,
            }}
            animate={{
              y: 0,
              x: sideBarIsOpen ? `300px` : `-50%`,
              left: sideBarIsOpen ? 0 : `50%`,
              borderRadius: sideBarIsOpen ? 0 : `0 0 8px 8px`,
            }}
            exit={{ y: `-100%` }}
            transition={{ duration: 0.2 }}
            style={{
              width: sideBarIsOpen
                ? `calc(100vw - 300px)`
                : `calc(100vw - 150px)`,
            }}
          >
            <section>
              <main>
                <NotificationIconWrapper onClick={() => setIsOpen(true)}>
                  <WarningIcon />
                </NotificationIconWrapper>
                <MessagesWrapper>
                  {notifications.map((notification) => {
                    return (
                      <div
                        data-testid="notification-bubble"
                        key={notification.key}
                        onClick={() => setIsOpen(false)}
                      >
                        <notification.message />
                      </div>
                    )
                  })}
                </MessagesWrapper>
              </main>
              <CloseIconWrapper>
                <CloseIcon onClick={() => setIsOpen(false)} />
              </CloseIconWrapper>
            </section>
          </Container>
        )}
      </AnimatePresence>
    </>
  )
}

export const NotificationBarContainer: FC = () => {
  const notifications = useRecoilValue(notificationsState)
  if (notifications.length === 0) return null

  return (
    <FloatingPortal>
      <NotificationBar />
    </FloatingPortal>
  )
}
